import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  TuiAlertService,
  TuiButtonModule,
  TuiLoaderModule,
  TuiTextfieldControllerModule,
  TuiSvgModule,
  TuiErrorModule,
} from '@taiga-ui/core';
import { TuiInputModule, TuiFieldErrorPipeModule } from '@taiga-ui/kit';
import { Router, ActivatedRoute } from '@angular/router';
import { NgIf, AsyncPipe } from '@angular/common';
import { InventoryService } from '../../../core/services/inventory.service';

@Component({
  selector: 'app-add-inventory',
  templateUrl: './add-inventory.component.html',
  styleUrls: ['./add-inventory.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    TuiButtonModule,
    TuiLoaderModule,
    TuiTextfieldControllerModule,
    TuiSvgModule,
    TuiInputModule,
    TuiErrorModule,
    TuiFieldErrorPipeModule,
  ],
})
export class AddInventoryComponent implements OnInit {
  form!: FormGroup;
  loading = false;

  /** Use a neutral name so we don't confuse it with the business "itemId" field */
  private recordId: string | null = null;
  isEditMode = false;

  constructor(
    private fb: FormBuilder,
    private alertService: TuiAlertService,
    public router: Router,
    private inventoryService: InventoryService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Build form
    this.form = this.fb.group({
      itemName: ['', Validators.required],
      quantity: ['', [Validators.required, Validators.min(1)]],
      price: ['', [Validators.required, Validators.min(0)]],
    });

    // Read backend _id from route param `id`
    this.recordId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.recordId;

    if (this.isEditMode && this.recordId) {
      this.loadItemData(this.recordId);
    }
  }

  async loadItemData(id: string): Promise<void> {
    try {
      const res = await this.inventoryService.getInventoryItem(id);
      const item = (res?.data ?? res) as any; // handle {data: {...}} or direct object

      if (item) {
        this.form.patchValue({
          // Map possible API keys to our form fields
          itemName: item.itemName ?? item.name ?? '',
          quantity: Number(item.quantity ?? item.qty ?? 0),
          price: Number(item.price ?? item.cost ?? 0),
        });
        // OnPush: ensure view updates for async patch
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Error loading item data:', error);
      this.alertService.open('Failed to load item data', { status: 'error' }).subscribe();
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    try {
      if (this.isEditMode && this.recordId) {
        await this.inventoryService.updateInventoryItem(this.recordId, this.form.value);
      } else {
        await this.inventoryService.createInventoryItem(this.form.value);
      }

      this.alertService
        .open(`Inventory item ${this.isEditMode ? 'updated' : 'added'}!`, { status: 'success' })
        .subscribe();

      this.router.navigate(['/admin/inventory']);
    } catch (err) {
      console.error(err);
      this.alertService
        .open(`Failed to ${this.isEditMode ? 'update' : 'add'} inventory item`, { status: 'error' })
        .subscribe();
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  cancel(): void {
    this.router.navigate(['/admin/inventory']);
  }
}
